import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, TablePagination } from "@mui/material";
import {
  IExportChatData,
  IExportChatDataFields,
} from "@home-health-notify/domain";
import ExportChatTableItem from "./ExportChatTableItem";
import {
  ExportChatTableContainer,
  ExportChatTableLayout,
} from "./ExportChatTable.style";
import { useTranslation } from "hhn-commons";

interface Column {
  id:
    | "sNo"
    | "patientName"
    | "episodeDate"
    | "type"
    | "exportRequestDate"
    | "status"
    | "action";
  label: string;
  width?: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  {
    id: "sNo",
    label: "No.",
    width: 50,
  },
  {
    id: "patientName",
    label: "Patient Name",
    width: 50,
  },
  {
    id: "episodeDate",
    label: "Episode Date",
    width: 185,
  },
  {
    id: "type",
    label: "Type",
    width: 80,
  },
  {
    id: "exportRequestDate",
    label: "Export Request Date",
    width: 150,
  },
  {
    id: "status",
    label: "Status",
    width: 40,
  },
  {
    id: "action",
    label: "Action",
    width: 40,
  },
];

type ExportChatTableProps = {
  page: number;
  onSetPage: (value: number) => void;
  rowsPerPage: number;
  onPageLimitChange: (pageLimit: number, pageNumber: number) => void;
  exportChats: IExportChatData;
};

export function ExportChatTable({
  page,
  onSetPage,
  rowsPerPage,
  onPageLimitChange,
  exportChats,
}: ExportChatTableProps) {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "exportChat" });

  const handleChangePage = (event: unknown, newPage: number) => {
    onSetPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const initialRowsPerPage = rowsPerPage;
    const pageLimit = +event.target.value;
    const pageNumber =
      Math.ceil(
        (initialRowsPerPage * (page + 1) - (initialRowsPerPage - 1)) /
          +event.target.value,
      ) - 1;
    onPageLimitChange(pageLimit, pageNumber);
  };

  return (
    <Box>
      <ExportChatTableLayout>
        <ExportChatTableContainer>
          <Table
            stickyHeader
            aria-label="export chat table"
            data-testid="export-chat-table"
          >
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    style={{ width: column.width, padding: "10px" }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {exportChats.data.map(
                (exportChat: IExportChatDataFields, index) => (
                  <ExportChatTableItem
                    key={exportChat.id}
                    exportChat={exportChat}
                    number={index + 1 + rowsPerPage * page}
                  />
                ),
              )}
              {exportChats.data.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{ textAlign: "center" }}
                  >
                    {tNs("noRecordFound")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ExportChatTableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={exportChats.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton={true}
          showLastButton={true}
        />
      </ExportChatTableLayout>
    </Box>
  );
}
